import axios, { AxiosError, type AxiosResponse, type InternalAxiosRequestConfig } from 'axios';
import brotliPromise from 'brotli-dec-wasm';

type BrotliApi = {
  decompress: (input: Uint8Array) => Uint8Array;
};

const brotliReady = brotliPromise as unknown as Promise<BrotliApi>;

function getApiBase(): string {
  const raw = import.meta.env.VITE_API_URL;
  if (typeof raw === 'string' && raw.trim() && !raw.includes('undefined')) {
    return raw.replace(/\/+$/, '');
  }
  return 'http://localhost:3000';
}

const API_URL = getApiBase();

export { getApiBase };
export const apiClient = axios.create({
  baseURL: API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

function isPlainPayload(bytes: Uint8Array): boolean {
  let i = 0;
  while (i < bytes.length && (bytes[i] === 32 || bytes[i] === 9 || bytes[i] === 10 || bytes[i] === 13)) {
    i += 1;
  }
  if (i >= bytes.length) return true;
  const c = bytes[i];
  if (c === 0x7b || c === 0x5b || c === 0x22 || c === 0x3c) return true;
  if (c === 0x74 || c === 0x66 || c === 0x6e || c === 0x2d) return true;
  return c >= 0x30 && c <= 0x39;
}

/** Le décodeur wasm n'est pas réentrant : une file évite les décodages parallèles. */
let decodeChain: Promise<unknown> = Promise.resolve();

function looksLikeImage(bytes: Uint8Array): boolean {
  if (bytes.length >= 3 && bytes[0] === 0xff && bytes[1] === 0xd8 && bytes[2] === 0xff) return true;
  if (bytes.length >= 8 && bytes[0] === 0x89 && bytes[1] === 0x50 && bytes[2] === 0x4e && bytes[3] === 0x47) {
    return true;
  }
  return (
    bytes.length >= 12 &&
    bytes[0] === 0x52 &&
    bytes[1] === 0x49 &&
    bytes[2] === 0x46 &&
    bytes[3] === 0x46 &&
    bytes[8] === 0x57 &&
    bytes[9] === 0x45 &&
    bytes[10] === 0x42 &&
    bytes[11] === 0x50
  );
}

async function decodeTransportBytesNow(bytes: Uint8Array): Promise<Uint8Array> {
  if (bytes.length === 0 || isPlainPayload(bytes) || looksLikeImage(bytes)) return bytes;
  try {
    const brotli = await brotliReady;
    return brotli.decompress(bytes);
  } catch {
    return bytes;
  }
}

/** Corps Brotli laissé tel quel par le edge (Content-Encoding: identity + br). */
export function decodeTransportBytes(bytes: Uint8Array): Promise<Uint8Array> {
  const run = decodeChain.then(() => decodeTransportBytesNow(bytes));
  decodeChain = run.then(
    () => undefined,
    () => undefined
  );
  return run;
}

function parseJsonBytes(bytes: Uint8Array): unknown {
  if (bytes.length === 0) return null;
  const text = new TextDecoder().decode(bytes);
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return text;
  }
}

async function decodePayload(data: unknown): Promise<unknown> {
  if (data instanceof ArrayBuffer) {
    return parseJsonBytes(await decodeTransportBytes(new Uint8Array(data)));
  }
  if (data instanceof Blob) {
    const raw = new Uint8Array(await data.arrayBuffer());
    const bytes = await decodeTransportBytes(raw);
    if (bytes === raw) return data;
    const copy = new ArrayBuffer(bytes.byteLength);
    new Uint8Array(copy).set(bytes);
    return new Blob([copy], { type: data.type });
  }
  return data;
}

apiClient.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem('cuxa_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  if (config.responseType == null || config.responseType === 'json') {
    config.responseType = 'arraybuffer';
  }
  return config;
});

apiClient.interceptors.response.use(
  async (response: AxiosResponse) => {
    response.data = await decodePayload(response.data);
    return response;
  },
  async (error: AxiosError) => {
    if (error.response) {
      error.response.data = await decodePayload(error.response.data);
    }
    if (error.response?.status === 401 && !error.config?.url?.includes('/api/auth/login')) {
      localStorage.removeItem('cuxa_token');
      const redirect = encodeURIComponent(window.location.pathname + window.location.search);
      window.location.href = redirect ? `/login?redirect=${redirect}` : '/login';
      return Promise.reject(error);
    }
    if (import.meta.env.DEV) {
      if (error.response) {
        console.error(
          `[API Error] ${error.config?.method?.toUpperCase()} ${error.config?.url}`,
          { status: error.response.status, data: error.response.data }
        );
      } else if (error.request) {
        console.error('[API Error] No response received', { url: error.config?.url });
      } else {
        console.error('[API Error] Request setup error', error.message);
      }
    }
    return Promise.reject(error);
  }
);
